import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { firebaseAnalyticsNO } from '../../firebase';
import amexsvg from '../../assets/images/amexsvg.svg';
import visasvg from '../../assets/images/visasvg.svg';
import mastercardsvg from '../../assets/images/mastercardsvg.svg';
import { Elements } from '@stripe/react-stripe-js';
import { PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import getStripePromise from '../../configuration/getStripePromise';
import { selectStoreConfig } from '../../containers/Layout/selectors';

import { ReactComponent as IconReceipt } from 'assets/images/icon-receipt.svg';
import { eventNames } from 'configuration/eventNames';
import { trackAddPaymentInfo, trackPurchase } from 'helpers/analytics';
import { getPlatformAndCardCharges } from 'helpers/getPlatformAndCardCharges';
import { getEffectivePreparationTimes } from 'helpers/orderValueTimeAdjust';
import getOrderObjectIfDinein from 'helpers/getOrderObjectIfDinein';
import {
  MENU_ORDER_TYPE,
  ORDER_PAYMENT_TYPE,
  ORDER_STAGE_TYPES,
} from 'helpers/enums';
import {
  BackButtonCWUI,
  ButtonUI,
  OrderUI,
  BadgeUI,
} from '@next-order/next-order-components';
import { useHistory } from 'react-router-dom';
import getCardCharges from 'helpers/getCardCharges';
import store from 'store';
import { ArrowRightIcon, LoadingIcon } from 'nexticons/outline';
import useRetryUntilResolved from 'helpers/hooks/useRetryUntilResolved';
import AdyenDropinPayment from './AdyenDropinPayment';
import getCardTypeFromName from 'helpers/getCardTypeFromName';
import exclamationMark from '../../assets/images/exclamationmark.svg';
import classNames from 'classnames';
import localStorage from 'local-storage';
import { useAdyen } from './AdyenProvider';

const getCurrentOrder = () => store.getState().homeReducer.currentOrder;
const getUserDetails = () => store.getState().homeReducer.userDetails;
const useConditionalAdyen = (enabled) => {
  const adyen = useAdyen();
  if (!enabled) {
    return {};
  }
  // Return adyen if it exists, otherwise return empty object with no-op functions
  if (!adyen) {
    console.warn(
      'useAdyen returned null - AdyenProvider may not be wrapping this component'
    );
    return {
      checkout: null,
      isLoading: false,
      mountComponent: () => {},
      setToPay: () => {},
    };
  }
  return adyen;
};

export default function OrderPayment({
  orderSetup,
  updateOrderStage,
  updatePaymentTypeForCash,
  updatePaymentTypeForWalletOrCard,
  fetchOrderHandler,
  pickUpTimes,
  deliveryTimes,
  updateDeliveryPickupTime,
  updateOrderStageBack,
  sidebarAnimationClass,
  className,
}) {
  const history = useHistory();

  const storeConfig = useSelector(selectStoreConfig);
  const { currentOrder, notes, userDetails, isMinifiedVersion } = useSelector(
    (state) => state.homeReducer
  );
  const creditCards = useSelector((state) => state.layoutReducer.creditCards);
  const requestOrderObjAdyenRef = useRef(null);
  const dropinRef = useRef(null);

  const [localState, setLocalState] = useReducer((s, a) => ({ ...s, ...a }), {
    moveNextDetail: 0,
    paymentInProcess: false,
    keyBoard: false,
    placeholderValue: 'MM/YY',
    isCardError: false,
    isError: false,
    errorMessage: '',
    clientSecret: '',
  });

  const handleResetAdyen = () => {
    setLocalState({
      isAdditionalDetailsInProcess: false,
      cardPaymentInProcess: false,
      isCardError: false,
      isError: false,
      errorMessage: '',
    });
    if (dropinRef.current) {
      dropinRef.current?.resetDropin();
    }
  };

  const getAmountForCard = useCallback(() => {
    const orderObj = JSON.parse(JSON.stringify(currentOrder));
    const { payableAmount } = orderObj;

    let creditCardCharges = 0;
    let platformSurchargeAmt = 0;

    if (orderSetup?.isCustomerLiableForOnlineCharges) {
      const { percentageCharge, fixedCharge } = getCardCharges({
        cards: creditCards,
      });
      creditCardCharges =
        parseFloat(payableAmount) * (percentageCharge / 100) + fixedCharge;
      creditCardCharges = Number(creditCardCharges).toFixed(2);
    }

    const payableAmountWithCreditCardCharges =
      +payableAmount + +creditCardCharges;

    if (
      orderSetup?.isPlatformSurchargeLevied &&
      orderSetup?.platformSurchargeAmt &&
      orderObj?.orderType !== MENU_ORDER_TYPE.dinein
    ) {
      const platformSurchargePercentage =
        Number(orderSetup.platformSurchargePercentage) || 0;
      platformSurchargeAmt =
        parseFloat(payableAmountWithCreditCardCharges) *
          (platformSurchargePercentage / 100) +
        Number(orderSetup.platformSurchargeAmt);
    }

    if (
      orderObj?.isGfoOrder &&
      orderSetup.gfoSurchargeEnabled &&
      orderObj.orderType !== MENU_ORDER_TYPE.dinein
    ) {
      const gfoSurchargePercentage = Number(orderSetup.gfoSurchargeRate) || 0;
      const gfoSurchargeAmt =
        (parseFloat(payableAmountWithCreditCardCharges) +
          parseFloat(platformSurchargeAmt)) *
        (gfoSurchargePercentage / 100);
      platformSurchargeAmt = platformSurchargeAmt + gfoSurchargeAmt;
    }

    platformSurchargeAmt = Number(platformSurchargeAmt).toFixed(2);

    const toPay = +payableAmount + +creditCardCharges + +platformSurchargeAmt;
    return parseInt((Number(toPay) * 100).toFixed(2), 10) || 0;
  }, [currentOrder, orderSetup, creditCards]);

  // componentDidMount
  useEffect(() => {
    window.navigator.userAgent.match(/android/i) &&
      window.addEventListener('resize', onElementHeightChange);
    if (
      orderSetup &&
      orderSetup.enableApplePay &&
      orderSetup.enableApplePay === true
    ) {
      getStripePromise(storeConfig.publishableKey).then((stripe) => {
        const orderObj = JSON.parse(JSON.stringify(currentOrder));
        const { payableAmount } = orderObj;

        let creditCardCharges = 0;
        let platformSurchargeAmt = 0;

        if (orderSetup?.isCustomerLiableForOnlineCharges) {
          const { percentageCharge, fixedCharge } = getCardCharges({
            cards: creditCards,
          });
          creditCardCharges =
            parseFloat(payableAmount) * (percentageCharge / 100) + fixedCharge;
          creditCardCharges = Number(creditCardCharges).toFixed(2);
        }

        const payableAmountWithCreditCardCharges =
          +payableAmount + +creditCardCharges;

        // Platform charges processing
        if (
          orderSetup.isPlatformSurchargeLevied &&
          orderSetup.platformSurchargeAmt &&
          orderObj.orderType !== MENU_ORDER_TYPE.dinein
        ) {
          const platformSurchargePercentage =
            Number(orderSetup.platformSurchargePercentage) || 0;
          platformSurchargeAmt =
            parseFloat(payableAmountWithCreditCardCharges) *
              (platformSurchargePercentage / 100) +
            Number(orderSetup.platformSurchargeAmt);
        }

        if (
          orderObj?.isGfoOrder &&
          orderSetup.gfoSurchargeEnabled &&
          orderObj.orderType !== MENU_ORDER_TYPE.dinein
        ) {
          const gfoSurchargePercentage =
            Number(orderSetup.gfoSurchargeRate) || 0;
          const gfoSurchargeAmt =
            (parseFloat(payableAmountWithCreditCardCharges) +
              parseFloat(platformSurchargeAmt)) *
            (gfoSurchargePercentage / 100);
          platformSurchargeAmt = platformSurchargeAmt + gfoSurchargeAmt;
        }

        platformSurchargeAmt = Number(platformSurchargeAmt).toFixed(2);

        const toPay =
          +payableAmount + +creditCardCharges + +platformSurchargeAmt;

        const paymentRequest = stripe.paymentRequest({
          country: storeConfig.countryIdentifier.toUpperCase(),
          currency: storeConfig.currencyCode.toLowerCase(),
          total: {
            label: 'Amount',
            amount: parseInt((Number(toPay) * 100).toFixed(2), 10) || 0,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        paymentRequest.on('cancel', (ev) => {
          setLocalState({
            paymentInProcess: false,
          });
        });

        paymentRequest.canMakePayment().then((result) => {
          if (result) {
            setLocalState({
              stripe,
              canMakePayment: !!result,
              paymentRequest: paymentRequest,
              onlyGooglePay: result.googlePay && !result.applePay,
            });
          }
        });
      });
    }
    return () => {
      window.navigator.userAgent.match(/android/i) &&
        window.removeEventListener('resize', onElementHeightChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (localState.clientSecret) {
      localState.paymentRequest.on('paymentmethod', async (ev) => {
        const stripeResponse = await localState.stripe.confirmCardPayment(
          localState.clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );
        const { error: confirmError, paymentIntent } = stripeResponse || {};
        console.log('paymentIntent: ', paymentIntent);

        if (confirmError) {
          let message = confirmError.message
            ? confirmError.message
            : 'Cannot Process Card. Something went wrong.';

          if (
            confirmError.code &&
            confirmError.code === 'card_declined' &&
            confirmError.decline_code &&
            confirmError.decline_code === 'insufficient_funds'
          ) {
            message = 'Your card has been declined.';
          }

          setLocalState({
            paymentInProcess: false,
            isError: true,
            errorMessage: message,
          });
          console.error('In Failed condition', confirmError);
          ev.complete('fail');
        } else {
          fetchOrderHandler(localState.preOrderId);
          history.replace(
            `/order-tracking/${localState.preOrderId}/${currentOrder?.orderType}`
          );
          ev.complete('success');
          trackPurchase(getCurrentOrder(), localState.preOrderId);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localState.clientSecret]);

  const updateSelectedTimeIfItIsInPast = useCallback(
    (order, availableTimes) => {
      if (order && order.deliveryDate && availableTimes && orderSetup) {
        let today = moment(
          moment().tz(storeConfig.timeZone).format('YYYY-MM-DD HH:mm'),
          'YYYY-MM-DD HH:mm'
        );
        // Effective prep = base + order-value extra minutes, matching the
        // slot arrays generated in Order/index.js.
        const effectivePrep = getEffectivePreparationTimes(
          orderSetup,
          order.payableAmount
        );
        today.add(
          order.orderType === MENU_ORDER_TYPE.pickup
            ? effectivePrep.pickUpTime
            : effectivePrep.deliveryTime,
          'minutes'
        );
        let delDate = moment(order.deliveryDate, 'YYYY-MM-DD HH:mm');

        //Detect if update required
        if (today.isAfter(delDate)) {
          if (
            availableTimes.length > 0 &&
            availableTimes[0].times &&
            availableTimes[0].times.length > 0
          ) {
            let timeObj = availableTimes[0].times[0];
            let deliveryDate = moment(
              `${timeObj.timeObjFinal} ${timeObj.displayForUse}`,
              'YYYY-MM-DD HH:mm A'
            );
            if (deliveryDate.isAfter(today)) {
              updateDeliveryPickupTime(
                deliveryDate,
                timeObj.shiftId,
                false,
                0,
                false
              );
            } else {
              updateDeliveryPickupTime(today, timeObj.shiftId, false, 0, false);
            }
          } else {
            // No time slot available
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orderSetup?.pickUpTime,
      orderSetup?.deliveryTime,
      orderSetup?.isOrderValueTimeAdjustEnabled,
      orderSetup?.orderTimeRules,
      updateDeliveryPickupTime,
      storeConfig.timeZone,
    ]
  );

  // UNSAFE_componentWillReceiveProps
  useEffect(() => {
    updateSelectedTimeIfItIsInPast(
      currentOrder,
      currentOrder.orderType === MENU_ORDER_TYPE.delivery
        ? deliveryTimes
        : pickUpTimes
    );
  }, [
    currentOrder,
    deliveryTimes,
    pickUpTimes,
    updateSelectedTimeIfItIsInPast,
  ]);

  const markAsCashPayment = () => {
    setLocalState({
      moveNextDetail: 0,
    });
    updatePaymentTypeForCash();
  };

  const markAsCardPaymentOptions = () => {
    updateOrderStage(ORDER_STAGE_TYPES.PAYMENT_CARD);
  };

  useEffect(() => {
    if (
      currentOrder &&
      Number(currentOrder.payableAmount) <= 0 &&
      currentOrder.stage === ORDER_STAGE_TYPES.PAYMENT
    ) {
      markAsCashPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentOrder)]);

  const fetchPlaceOrder = async (orderObj3d, isAppleGooglePay = false) => {
    const cloudFunction = isAppleGooglePay
      ? 'placeOrderApplePayV2'
      : 'placeOrder3DSecureV2';
    const response = await fetch(
      `${storeConfig.cloudFunctionsUrl}/${cloudFunction}`,
      {
        method: 'post',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...orderObj3d,
        }),
      }
    );
    const data = await response.json();
    return { ...data, isAppleGooglePay };
  };

  const { data, isLoading, retry } = useRetryUntilResolved({
    conditionForRetry: (data) => data?.code === 'invalid_iteration',
  });

  const placeOrderWithAdyen = async (
    currOrder = currentOrder,
    adyenData,
    isAppleGooglepay
  ) => {
    setLocalState({
      paymentInProcess: true,
      isError: false,
      errorMessage: '',
    });

    const latestUserDetails = getUserDetails();
    let orderObj = JSON.parse(JSON.stringify(currOrder));
    if (orderObj.isAllowToConfirm !== undefined)
      delete orderObj.isAllowToConfirm;
    if (orderObj.offers) delete orderObj.offers;
    if (orderObj.stage) delete orderObj.stage;
    if (typeof orderObj.isFreeItemsAvailable !== 'undefined')
      delete orderObj.isFreeItemsAvailable;
    let {
      payableAmount,
      creditCardCharges,
      isPlatformSurchargeLevied,
      platformSurchargePercentage,
      platformSurchargeAmt,
    } = orderObj;

    // Credit card processing
    if (orderSetup?.isCustomerLiableForOnlineCharges) {
      const { percentageCharge, fixedCharge } = getCardCharges({
        cards: creditCards,
        cardType: orderObj?.cardType,
        isInternationCard: orderObj?.isInternationCard,
      });
      creditCardCharges =
        parseFloat(payableAmount) * (percentageCharge / 100) + fixedCharge;

      creditCardCharges = Number(creditCardCharges).toFixed(2);
    }

    const payableAmountWithCreditCardCharges =
      +payableAmount + +creditCardCharges;

    // Platform charges processing
    if (
      orderSetup?.isPlatformSurchargeLevied &&
      orderSetup?.platformSurchargeAmt &&
      orderObj?.orderType !== MENU_ORDER_TYPE.dinein
    ) {
      isPlatformSurchargeLevied = true;
      platformSurchargePercentage =
        Number(orderSetup.platformSurchargePercentage) || 0;
      platformSurchargeAmt =
        parseFloat(payableAmountWithCreditCardCharges) *
          (platformSurchargePercentage / 100) +
        Number(orderSetup.platformSurchargeAmt);
    }

    if (
      orderObj?.isGfoOrder &&
      orderSetup.gfoSurchargeEnabled &&
      orderObj.orderType !== MENU_ORDER_TYPE.dinein
    ) {
      const gfoSurchargePercentage = Number(orderSetup.gfoSurchargeRate) || 0;
      const gfoSurchargeAmt =
        (parseFloat(payableAmountWithCreditCardCharges) +
          parseFloat(platformSurchargeAmt)) *
        (gfoSurchargePercentage / 100);
      platformSurchargeAmt = platformSurchargeAmt + gfoSurchargeAmt;
      platformSurchargeAmt = Number(platformSurchargeAmt).toFixed(2);
    }

    const toPayWithPlatformCharges = localStorage.get('toPay');

    const toPay = +payableAmount + +creditCardCharges;
    const orderData = {
      ...orderObj,
      creditCardCharges: Number(creditCardCharges).toFixed(2),
      platformSurchargeAmt: Number(platformSurchargeAmt).toFixed(2),
      platformSurchargePercentage: Number(platformSurchargePercentage).toFixed(
        2
      ),
      isPlatformSurchargeLevied: isPlatformSurchargeLevied,
      payableAmount: toPay.toFixed(2),
      paymentType: ORDER_PAYMENT_TYPE.card_online,
      minOrderValue: `${orderObj.minOrderValue || 0}`,
      userId: latestUserDetails?._id,
      firstName: latestUserDetails?.firstName || '',
      lastName: latestUserDetails?.lastName || '',
      email: latestUserDetails?.email || '',
      storeId: storeConfig.storeId,
      restaurantId: storeConfig.restaurantId,
      notes: notes || orderObj.notes,
    };

    const paymentData = {
      merchantCode: 500,
      amount: parseInt((Number(toPay) * 100).toFixed(2), 10),
    };

    let requestObj = {
      order: orderData,
      payment: {
        ...paymentData,
        ...adyenData,
      },
      toPay: toPayWithPlatformCharges,
    };

    const adyenOrderObject = {
      order: orderData,
      payment: paymentData,
    };

    localStorage.set('adyenOrderObject', adyenOrderObject);

    const orderObj3d = await getOrderObjectIfDinein(requestObj, orderSetup);
    const fetchOrder = await fetchPlaceOrder(orderObj3d, isAppleGooglepay);
    const orderId = fetchOrder?.orderId || '';
    requestOrderObjAdyenRef.current = { ...orderObj3d, orderId };
    return { ...fetchOrder, orderType: orderObj.orderType };
  };

  const placeOrder = async (currOrder = currentOrder) => {
    if (
      !(currOrder && currOrder.paymentType && userDetails && userDetails._id) ||
      !(
        currOrder.paymentType === ORDER_PAYMENT_TYPE.cash &&
        !localState.paymentInProcess
      )
    ) {
      return;
    }

    setLocalState({
      isCardError: false,
      paymentInProcess: true,
    });

    let orderObj = JSON.parse(JSON.stringify(currOrder));
    if (orderObj.isAllowToConfirm !== undefined)
      delete orderObj.isAllowToConfirm;
    if (orderObj.offers) delete orderObj.offers;
    if (orderObj.stage) delete orderObj.stage;
    if (typeof orderObj.isFreeItemsAvailable !== 'undefined')
      delete orderObj.isFreeItemsAvailable;

    let requestObj = {
      order: {
        ...orderObj,
        gst: currOrder.gst || '0.00',
        isPriceIncludesGST: currOrder.isPriceIncludesGST || false,
        notes: notes || currOrder.notes,
        userId: userDetails._id,
        firstName: userDetails.firstName ? userDetails.firstName : '',
        lastName: userDetails.lastName ? userDetails.lastName : '',
        email: userDetails.email ? userDetails.email : '',
        storeId: storeConfig.storeId,
        restaurantId: storeConfig.restaurantId,
        paymentType: ORDER_PAYMENT_TYPE.unpaid, //unpaid cash
        paymentCollectionType: '1', //in-cash
        minOrderValue: `${orderObj.minOrderValue || 0}`,
      },
      payment: {
        type: 0,
        amount: parseInt((Number(orderObj.payableAmount) * 100).toFixed(2), 10),
      },
    };

    retry(async () => {
      const orderObj3d = await getOrderObjectIfDinein(requestObj, orderSetup);
      const fetchOrder = await fetchPlaceOrder(orderObj3d);
      return { ...fetchOrder, orderType: orderObj.orderType };
    });
  };

  useEffect(() => {
    if (!isLoading) {
      if (data?.success) {
        if (data.isAppleGooglePay) {
          setLocalState({
            clientSecret: data.payload?.secret,
            preOrderId: data.payload?.preOrder?.preOrderId,
          });
        } else {
          fetchOrderHandler(data.orderId);
          history.replace(`/order-tracking/${data.orderId}/${data.orderType}`);
          trackPurchase(getCurrentOrder(), data.orderId);
        }
      } else {
        setLocalState({
          isError: true,
          errorMessage: data?.message,
          paymentInProcess: false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);

  const onElementHeightChange = (height) => {
    let lastHeight = height;
    let newHeight = window.innerHeight;
    if (lastHeight - newHeight > 125) {
      setLocalState({ keyBoard: true, moveNextDetail: 11 });
    } else {
      if (localState.keyBoard === true && lastHeight - newHeight === 0) {
        setLocalState({ keyBoard: false, moveNextDetail: 0 });
      }
    }
  };

  const onAppleGooglePayClick = () => {
    updatePaymentTypeForWalletOrCard();
    updateSelectedTimeIfItIsInPast(
      currentOrder,
      currentOrder.orderType === MENU_ORDER_TYPE.delivery
        ? deliveryTimes
        : pickUpTimes
    );
    setTimeout(() => {
      const latestCurrentOrder = getCurrentOrder();
      onPaymentButtonClick(latestCurrentOrder);
    }, 0);
  };

  const onPaymentButtonClick = async (currOrder = currentOrder) => {
    // Create a payment Intent and then Show
    setLocalState({
      isCardError: false,
      paymentInProcess: true,
      isError: false,
      errorMessage: '',
    });

    let orderObj = JSON.parse(JSON.stringify(currOrder));
    if (orderObj.isAllowToConfirm !== undefined)
      delete orderObj.isAllowToConfirm;
    if (orderObj.offers) delete orderObj.offers;
    if (orderObj.stage) delete orderObj.stage;
    if (typeof orderObj.isFreeItemsAvailable !== 'undefined')
      delete orderObj.isFreeItemsAvailable;

    let { payableAmount, creditCardCharges } = orderObj;

    if (
      !creditCardCharges ||
      (creditCardCharges && !Number(creditCardCharges))
    ) {
      if (orderSetup?.isCustomerLiableForOnlineCharges) {
        const { percentageCharge, fixedCharge } = getCardCharges({
          cards: creditCards,
          cardType: orderObj?.cardType,
          isInternationCard: orderObj?.isInternationCard,
        });
        creditCardCharges =
          parseFloat(payableAmount) * (percentageCharge / 100) + fixedCharge;
        creditCardCharges = Number(creditCardCharges).toFixed(2);
      }
    }

    const toPay = +payableAmount + +creditCardCharges;

    let requestObj = {
      order: {
        ...orderObj,
        creditCardCharges,
        payableAmount: toPay.toFixed(2),
        paymentType: ORDER_PAYMENT_TYPE.card_online,
        minOrderValue: `${orderObj.minOrderValue || 0}`,
        userId: userDetails?._id,
        firstName: userDetails?.firstName || '',
        lastName: userDetails?.lastName || '',
        email: userDetails.email || '',
        storeId: storeConfig.storeId,
        restaurantId: storeConfig.restaurantId,
      },
      payment: {
        type: 2,
        amount: parseInt((Number(toPay) * 100).toFixed(2), 10),
      },
    };

    try {
      localState.paymentRequest.show();
    } catch (e) {
      console.log(`paymentRequest show error`);
      console.error(e);
    }

    console.log(`placeOrderApplePay stage`);

    // Check if its a dinein order
    console.log('place order apple pay');

    retry(async () => {
      const orderObj3d = await getOrderObjectIfDinein(requestObj, orderSetup);
      const fetchOrder = await fetchPlaceOrder(orderObj3d, true);
      return { ...fetchOrder, orderType: orderObj.orderType };
    });
  };

  useEffect(() => {
    const { cardChargesTitle, cardCharges } =
      getPlatformAndCardCharges(currentOrder);
    setLocalState({ cardChargesTitle, cardCharges });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder?.paymentType]);

  useEffect(() => {
    if (currentOrder?.stage === ORDER_STAGE_TYPES.PAYMENT) {
      updatePaymentType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder?.stage]);

  const isDisabled =
    localState.paymentInProcess ||
    !(userDetails && userDetails._id) ||
    !currentOrder.paymentType ||
    currentOrder.paymentType !== ORDER_PAYMENT_TYPE.cash ||
    localState.paymentInProcess;

  const isCardPaymentAvailable =
    orderSetup?.isCardPaymentAccepted &&
    !(
      currentOrder?.orderType === MENU_ORDER_TYPE.dinein &&
      orderSetup?.isCardPaymentDisabledInDinein
    );
  const isAppleGooglePayAvailable = !(
    currentOrder?.orderType === MENU_ORDER_TYPE.dinein &&
    orderSetup?.isCardPaymentDisabledInDinein
  );
  const isAdyen =
    window.storeConfig?.isAdyenEcomPaymentsEnabled ||
    window.storeConfigInitial?.isAdyenEcomPaymentsEnabled;
  const toPay = getAmountForCard();
  const { setToPay } = useConditionalAdyen(isAdyen);

  useEffect(() => {
    if (isAdyen && !isMinifiedVersion && toPay > 0) {
      setToPay(toPay);
      localStorage.set('toPay', toPay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdyen, toPay, isMinifiedVersion]);

  const updatePaymentType = () => {
    const latestCurrentOrder = getCurrentOrder();
    if (latestCurrentOrder?.paymentType !== ORDER_PAYMENT_TYPE.card_online) {
      firebaseAnalyticsNO.logEvent(eventNames.SELECTED_PAYMENT_METHOD);
      trackAddPaymentInfo(getCurrentOrder());
      updatePaymentTypeForWalletOrCard('', latestCurrentOrder);
    }
  };

  const handleAdyenClick = () => {
    updatePaymentType();
  };

  const onSubmit = async (state, component, actions) => {
    component.setStatus('loading');
    const stateData = state?.data || {};
    const { paymentMethod } = stateData;
    if (paymentMethod) {
      const cardType = paymentMethod?.brand
        ? getCardTypeFromName(paymentMethod?.brand)?.type
        : '';
      try {
        const latestCurrentOrder = getCurrentOrder();
        updatePaymentTypeForWalletOrCard(cardType, latestCurrentOrder);
        setTimeout(() => {
          const latestCurrentOrder = getCurrentOrder();
          updateSelectedTimeIfItIsInPast(
            latestCurrentOrder,
            latestCurrentOrder.orderType === MENU_ORDER_TYPE.delivery
              ? deliveryTimes
              : pickUpTimes
          );
        }, 0);

        setTimeout(async () => {
          const type = paymentMethod?.type;
          const isAppleGooglePay = type === 'applepay' || type === 'googlepay';
          const storePaymentMethod = !(
            isAppleGooglePay || paymentMethod?.storedPaymentMethodId
          );
          const latestCurrentOrder = getCurrentOrder();
          const adyenData = {
            ...stateData,
            ...(storePaymentMethod && { storePaymentMethod }),
          };
          const data = await placeOrderWithAdyen(
            latestCurrentOrder,
            adyenData,
            isAppleGooglePay
          );

          if (isAppleGooglePay) {
            component.setStatus('success');
          }

          if (data?.payload?.action) {
            component.handleAction(data.payload.action);
            setTimeout(() => {
              setLocalState({
                cardPaymentInProcess: true,
              });
            }, 0);
          } else if (data?.orderId) {
            actions.resolve({ resultCode: 'Authorised' });
            fetchOrderHandler(data.orderId);
            firebaseAnalyticsNO.logEvent(eventNames.PAID_FOR_ORDER);
            trackPurchase(latestCurrentOrder, data.orderId);
            history.replace(
              `/order-tracking/${data.orderId}/${data.orderType}`
            );
          } else {
            actions.reject({ resultCode: 'Refused' });
            setLocalState({
              isCardError: true,
              isError: true,
              errorMessage: data?.message || 'Payment failed',
              paymentInProcess: false,
            });
            actions.resolve();
            component.setStatus('ready');
          }
        }, 10);
      } catch (error) {
        actions.reject({ resultCode: 'Refused' });
        setLocalState({
          isCardError: true,
          isError: true,
          errorMessage: 'An error occurred during payment',
          paymentInProcess: false,
        });
        component.setStatus('ready');
      }
    }
  };

  const onAdditionalDetails = async (state, component, actions) => {
    component.setStatus('loading');
    setLocalState({
      isAdditionalDetailsInProcess: true,
    });
    try {
      if (state.data) {
        const response = await fetch(
          `${storeConfig.cloudFunctionsUrl}/adyenPaymentDetails`,
          {
            method: 'post',
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...state.data,
              storeId: storeConfig.storeId,
              orderId: requestOrderObjAdyenRef.current?.orderId,
            }),
          }
        );
        const data = await response.json();

        if (data.success) {
          component.setStatus('success');
          actions.resolve({ resultCode: 'Authorised' });
          const requestOrderObjAdyen = requestOrderObjAdyenRef?.current;
          const orderId = requestOrderObjAdyen?.orderId;
          const orderType = requestOrderObjAdyen?.order?.orderType;

          fetchOrderHandler(orderId);
          firebaseAnalyticsNO.logEvent(eventNames.PAID_FOR_ORDER);
          trackPurchase(getCurrentOrder(), orderId);
          history.replace(`/order-tracking/${orderId}/${orderType}`);
        } else {
          actions.reject({ resultCode: 'Refused' });
          setLocalState({
            isCardError: true,
            isError: true,
            errorMessage: data?.message || 'Payment failed',
            paymentInProcess: false,
          });
          actions.resolve();
          component.setStatus('ready');
        }
      }
    } catch (error) {
      actions.reject({ resultCode: 'Refused' });
      setLocalState({
        isCardError: true,
        isError: true,
        errorMessage: 'An error occurred during payment',
        paymentInProcess: false,
      });
      component.setStatus('ready');
    } finally {
      setLocalState({
        isAdditionalDetailsInProcess: false,
      });
      component.setStatus('ready');
    }
  };

  const onPaymentCompleted = (result) => {
    console.log('onPaymentCompleted: ', result);
  };

  const onFailedorError = (from, data) => {
    console.error(from, data);
    setLocalState({
      isCardError: true,
      isError: true,
      errorMessage: 'An error occurred during payment',
      paymentInProcess: false,
      cardPaymentInProcess: false,
    });
  };

  const onPaymentFailed = (result) => {
    onFailedorError('onPaymentFailed: ', result);
  };

  const onError = (error) => {
    onFailedorError('onError: ', error);
  };

  if (isAdyen) {
    return (
      <div
        className={classNames(
          `${sidebarAnimationClass} cash detailsConfirm detailFinal active`,
          className
        )}
      >
        <div
          className={`moveNext${localState.moveNextDetail} detailsConfirm detailFinal detailPayment active`}
        >
          {localState.isAdditionalDetailsInProcess && (
            <div className='!fixed !h-full inset-0 z-50 bg-white opacity-70 flex flex-col items-center justify-center text-primary'>
              <LoadingIcon strokeWidth={3} width={40} />
              <p className='mt-3 text-base text-black'>
                Processing your payment securely...
              </p>
            </div>
          )}
          <BackButtonCWUI onClick={updateOrderStageBack} />
          <div>
            <div className='detailsTitle' style={{ marginTop: 0 }}>
              Payment Methods
            </div>
            {localState.isError && (
              <div className='cardCommon flex flex-col items-start gap-y-2 p-4 mb-5 rounded-lg'>
                <div>
                  <img src={exclamationMark} alt='error' className='h-[20px]' />
                </div>
                <span className='redSpan text-[14px] font-medium'>
                  {localState.errorMessage ? `${localState.errorMessage}` : ''}
                </span>
                {localState.isCardError && (
                  <>
                    <span className='text-[14px]'>
                      Please double check your card details or try a different
                      payment method.
                    </span>
                    <span className='text-[14px]'>
                      If the issue persists, contact your card issuer and try
                      again later.
                    </span>
                    <ButtonUI
                      onClick={handleResetAdyen}
                      btnType='reset'
                      size='lg'
                      extendedClassName='w-full !bg-black !border-black hover:text-white hover:opacity-90 mt-2 h-[42px] rounded-xl'
                    >
                      Retry Payment
                    </ButtonUI>
                  </>
                )}
              </div>
            )}
            {currentOrder && Number(currentOrder.payableAmount) <= 0 ? (
              // Show only Free option when payable amount is zero or less
              <div
                className={
                  currentOrder &&
                  currentOrder.paymentType &&
                  currentOrder.paymentType === ORDER_PAYMENT_TYPE.cash
                    ? 'cardCommon cash !mb-3 active'
                    : 'cardCommon cash !mb-3'
                }
                onClick={() => {
                  markAsCashPayment();
                  if (currentOrder.orderType === MENU_ORDER_TYPE.dinein) {
                    setTimeout(() => {
                      const latestCurrentOrder = getCurrentOrder();
                      firebaseAnalyticsNO.logEvent(
                        eventNames.SELECTED_PAYMENT_METHOD
                      );
                      trackAddPaymentInfo(getCurrentOrder());
                      updateSelectedTimeIfItIsInPast(
                        latestCurrentOrder,
                        latestCurrentOrder.orderType ===
                          MENU_ORDER_TYPE.delivery
                          ? deliveryTimes
                          : pickUpTimes
                      );
                      setTimeout(() => {
                        const latestCurrentOrder2 = getCurrentOrder();
                        placeOrder(latestCurrentOrder2);
                      }, 0);
                    }, 0);
                  }
                }}
              >
                <span>Free</span> <i className='icon-Cash' />
              </div>
            ) : (
              <>
                <AdyenDropinPayment
                  onClick={handleAdyenClick}
                  ref={dropinRef}
                  isCardPaymentAvailable={isCardPaymentAvailable}
                  onSubmit={onSubmit}
                  onAdditionalDetails={onAdditionalDetails}
                  onPaymentCompleted={onPaymentCompleted}
                  onPaymentFailed={onPaymentFailed}
                  onError={onError}
                />

                {!localState.cardPaymentInProcess &&
                  !localState.isCardError &&
                  orderSetup &&
                  currentOrder &&
                  currentOrder.orderType &&
                  ((currentOrder.orderType === MENU_ORDER_TYPE.pickup &&
                    orderSetup.isCashPaymentAccepted) ||
                    (currentOrder.orderType === MENU_ORDER_TYPE.delivery &&
                      orderSetup.isCashPaymentAcceptedDelivery) ||
                    (currentOrder.orderType === MENU_ORDER_TYPE.dinein &&
                      orderSetup.isCashPaymentAcceptedDineIn)) &&
                  ((orderSetup.maxCashOrderValue &&
                    Number(orderSetup.maxCashOrderValue) === 0) ||
                    (orderSetup.maxCashOrderValue &&
                      Number(orderSetup.maxCashOrderValue) !== 0 &&
                      Number(currentOrder.payableAmount) <=
                        Number(orderSetup.maxCashOrderValue))) && (
                    <div
                      className={
                        currentOrder &&
                        currentOrder.paymentType &&
                        currentOrder.paymentType === ORDER_PAYMENT_TYPE.cash
                          ? 'cardCommon cash !mb-3 active'
                          : 'cardCommon cash !mb-3'
                      }
                      onClick={() => {
                        markAsCashPayment();
                        if (currentOrder.orderType === MENU_ORDER_TYPE.dinein) {
                          setTimeout(() => {
                            const latestCurrentOrder = getCurrentOrder();
                            firebaseAnalyticsNO.logEvent(
                              eventNames.SELECTED_PAYMENT_METHOD
                            );
                            trackAddPaymentInfo(getCurrentOrder());
                            updateSelectedTimeIfItIsInPast(
                              latestCurrentOrder,
                              latestCurrentOrder.orderType ===
                                MENU_ORDER_TYPE.delivery
                                ? deliveryTimes
                                : pickUpTimes
                            );
                            setTimeout(() => {
                              const latestCurrentOrder2 = getCurrentOrder();
                              placeOrder(latestCurrentOrder2);
                            }, 0);
                          }, 0);
                        }
                      }}
                    >
                      <span>
                        {currentOrder.orderType === MENU_ORDER_TYPE.dinein
                          ? 'Pay at end of Sitting'
                          : 'Cash'}
                      </span>{' '}
                      {currentOrder.orderType === MENU_ORDER_TYPE.dinein ? (
                        <i className='icon-Receipt'>
                          <IconReceipt />
                        </i>
                      ) : (
                        <i className='icon-Cash' />
                      )}
                    </div>
                  )}
              </>
            )}

            {!localState.isCardError &&
              localState.cardCharges &&
              currentOrder?.paymentType !== ORDER_PAYMENT_TYPE.cash && (
                <div className='flex justify-center mt-3'>
                  <BadgeUI
                    title={`${localState.cardChargesTitle}: ${storeConfig.currency}${localState.cardCharges}`}
                    color='primary'
                    variant='self-colored'
                    fontWeight='normal'
                    fontSize='sm'
                    extendedClassName='card-fee-buuble !text-black'
                    extendedInnerClassName='normal-case'
                  />
                </div>
              )}
          </div>
        </div>
        <div className='totalbottom'>
          <div className='totalInner'>
            {currentOrder.publicHolidaySurcharge &&
            Number(currentOrder.publicHolidaySurcharge) !== 0 ? (
              <div className='spWrap'>
                <span className='spName'>
                  {currentOrder?.publicHolidayName
                    ? currentOrder?.publicHolidayName
                    : 'Public Holiday Surcharge'}
                  {`${
                    currentOrder &&
                    currentOrder.publicHolidaySurchargePer &&
                    Number(currentOrder.publicHolidaySurchargePer) !== 0
                      ? `(${currentOrder.publicHolidaySurchargePer}%)`
                      : null
                  }`}
                </span>
                <span className='spPrice'>{`${storeConfig.currency}${currentOrder.publicHolidaySurcharge}`}</span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder.rewards && currentOrder.rewards.length > 0 ? (
              <div className='spWrap'>
                <span className='spName'>Loyalty</span>
                <span className='spPrice'>{`- ${storeConfig.currency}${currentOrder.loyaltyDiscount}`}</span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder.specials &&
            currentOrder.specials.length > 0 &&
            Number(currentOrder.specialDiscount) > 0 ? (
              <div className='spWrap'>
                <span className='spName'>Specials</span>
                <span className='spPrice'>{`- ${storeConfig.currency}${currentOrder.specialDiscount}`}</span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder.voucherId &&
            currentOrder.voucherId !== '' &&
            Number(currentOrder.voucherDiscount) !== 0 ? (
              <div className='spWrap'>
                <span className='spName'>Voucher</span>
                <span className='spPrice'>{`- ${storeConfig.currency}${currentOrder.voucherDiscount} `}</span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder.isDisplayGST &&
            !currentOrder.isPriceIncludesGST &&
            Number(currentOrder.gst) !== 0 ? (
              <div className='spWrap'>
                <span className='spName'>GST</span>
                <span className='spPrice'>{` ${storeConfig.currency}${currentOrder.gst} `}</span>
              </div>
            ) : (
              <div />
            )}
            {localState.cardCharges &&
            currentOrder?.paymentType !== ORDER_PAYMENT_TYPE.cash ? (
              <div className='spWrap'>
                <span className='spName'>{localState.cardChargesTitle}:</span>
                <span className='spPrice'>
                  &nbsp;{`${storeConfig.currency}${localState.cardCharges}`}
                </span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder?.paymentType === ORDER_PAYMENT_TYPE.cash && (
              <OrderUI.FooterButton
                extendedClassName='z-[1003]'
                rightIcon={
                  localState.paymentInProcess ? (
                    <LoadingIcon strokeWidth={3} width={16} />
                  ) : (
                    <ArrowRightIcon strokeWidth={3} width={16} />
                  )
                }
                onClick={() => {
                  firebaseAnalyticsNO.logEvent(
                    eventNames.SELECTED_PAYMENT_METHOD
                  );
                  trackAddPaymentInfo(getCurrentOrder());
                  updateSelectedTimeIfItIsInPast(
                    currentOrder,
                    currentOrder.orderType === MENU_ORDER_TYPE.delivery
                      ? deliveryTimes
                      : pickUpTimes
                  );
                  setTimeout(() => {
                    const latestCurrentOrder = getCurrentOrder();
                    placeOrder(latestCurrentOrder);
                  }, 0);
                }}
                disabled={isDisabled}
              >
                {localState.paymentInProcess
                  ? 'Wait while we are processing'
                  : currentOrder && Number(currentOrder.payableAmount) <= 0
                  ? 'Confirm Order'
                  : `Confirm and Pay ${
                      currentOrder && currentOrder.payableAmount
                        ? `${storeConfig.currency}${currentOrder.payableAmount}`
                        : `${storeConfig.currency}0.00`
                    }`}
              </OrderUI.FooterButton>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={getStripePromise(storeConfig.publishableKey)}>
      <div
        className={`${sidebarAnimationClass} cash detailsConfirm detailFinal active`}
      >
        <div
          className={`moveNext${localState.moveNextDetail} detailsConfirm detailFinal detailPayment active`}
        >
          <BackButtonCWUI onClick={updateOrderStageBack} />
          <div>
            <div className='detailsTitle' style={{ marginTop: 0 }}>
              Payment Method
            </div>

            {currentOrder && Number(currentOrder.payableAmount) <= 0 ? (
              // Show only Free option when payable amount is zero or less
              <div
                className={
                  currentOrder &&
                  currentOrder.paymentType &&
                  currentOrder.paymentType === ORDER_PAYMENT_TYPE.cash
                    ? 'cardCommon cash active'
                    : 'cardCommon cash'
                }
                onClick={() => {
                  markAsCashPayment();
                  if (currentOrder.orderType === MENU_ORDER_TYPE.dinein) {
                    setTimeout(() => {
                      const latestCurrentOrder = getCurrentOrder();
                      firebaseAnalyticsNO.logEvent(
                        eventNames.SELECTED_PAYMENT_METHOD
                      );
                      trackAddPaymentInfo(getCurrentOrder());
                      updateSelectedTimeIfItIsInPast(
                        latestCurrentOrder,
                        latestCurrentOrder.orderType ===
                          MENU_ORDER_TYPE.delivery
                          ? deliveryTimes
                          : pickUpTimes
                      );
                      setTimeout(() => {
                        const latestCurrentOrder2 = getCurrentOrder();
                        placeOrder(latestCurrentOrder2);
                      }, 0);
                    }, 0);
                  }
                }}
              >
                <span>Free</span> <i className='icon-Cash' />
              </div>
            ) : (
              <>
                {isCardPaymentAvailable && (
                  <div
                    className={
                      currentOrder &&
                      currentOrder.paymentType &&
                      currentOrder.paymentType ===
                        ORDER_PAYMENT_TYPE.card_online
                        ? 'cardCommon cash'
                        : 'cardCommon cash'
                    }
                    onClick={markAsCardPaymentOptions}
                  >
                    <span
                      className='credit-card-span'
                      style={{ display: 'block' }}
                    >
                      Credit Card
                    </span>
                    {orderSetup?.isCardPaymentAccepted && (
                      <div className='img'>
                        {orderSetup?.isCustomerLiableForOnlineCharges && (
                          <div className='surcharge-container mr-2.5'>
                            <span className='surcharge-text'>
                              Surcharge Applies
                            </span>
                          </div>
                        )}
                        <img src={amexsvg} alt='' />
                        <img src={visasvg} alt='' />
                        <img src={mastercardsvg} alt='' />
                      </div>
                    )}
                  </div>
                )}
                {orderSetup &&
                  currentOrder &&
                  currentOrder.orderType &&
                  ((currentOrder.orderType === MENU_ORDER_TYPE.pickup &&
                    orderSetup.isCashPaymentAccepted) ||
                    (currentOrder.orderType === MENU_ORDER_TYPE.delivery &&
                      orderSetup.isCashPaymentAcceptedDelivery) ||
                    (currentOrder.orderType === MENU_ORDER_TYPE.dinein &&
                      orderSetup.isCashPaymentAcceptedDineIn)) &&
                  ((orderSetup.maxCashOrderValue &&
                    Number(orderSetup.maxCashOrderValue) === 0) ||
                    (orderSetup.maxCashOrderValue &&
                      Number(orderSetup.maxCashOrderValue) !== 0 &&
                      Number(currentOrder.payableAmount) <=
                        Number(orderSetup.maxCashOrderValue))) && (
                    <div
                      className={
                        currentOrder &&
                        currentOrder.paymentType &&
                        currentOrder.paymentType === ORDER_PAYMENT_TYPE.cash
                          ? 'cardCommon cash active'
                          : 'cardCommon cash'
                      }
                      onClick={() => {
                        markAsCashPayment();
                        if (currentOrder.orderType === MENU_ORDER_TYPE.dinein) {
                          setTimeout(() => {
                            const latestCurrentOrder = getCurrentOrder();
                            firebaseAnalyticsNO.logEvent(
                              eventNames.SELECTED_PAYMENT_METHOD
                            );
                            trackAddPaymentInfo(getCurrentOrder());
                            updateSelectedTimeIfItIsInPast(
                              latestCurrentOrder,
                              latestCurrentOrder.orderType ===
                                MENU_ORDER_TYPE.delivery
                                ? deliveryTimes
                                : pickUpTimes
                            );
                            setTimeout(() => {
                              const latestCurrentOrder2 = getCurrentOrder();
                              placeOrder(latestCurrentOrder2);
                            }, 0);
                          }, 0);
                        }
                      }}
                    >
                      <span>
                        {currentOrder.orderType === MENU_ORDER_TYPE.dinein
                          ? 'Pay at end of Sitting'
                          : 'Cash'}
                      </span>{' '}
                      {currentOrder.orderType === MENU_ORDER_TYPE.dinein ? (
                        <i className='icon-Receipt'>
                          <IconReceipt />
                        </i>
                      ) : (
                        <i className='icon-Cash' />
                      )}
                    </div>
                  )}

                {isAppleGooglePayAvailable && (
                  <>
                    {localState.canMakePayment &&
                      localState.onlyGooglePay === false && (
                        <React.Fragment>
                          <PaymentRequestButtonElement
                            options={{
                              paymentRequest: localState.paymentRequest,
                            }}
                            className='PaymentRequestButton'
                            onClick={(evt) => {
                              evt?.preventDefault();
                              onAppleGooglePayClick();
                            }}
                            disabled={!(userDetails && userDetails._id)}
                          />
                        </React.Fragment>
                      )}

                    {localState.canMakePayment &&
                      localState.onlyGooglePay === true && (
                        <React.Fragment>
                          {/* Show Custom Google pay Button */}
                          <div style={{ height: '64px', width: '100%' }}>
                            <button
                              type='button'
                              aria-label='Google Pay'
                              className='gpay-button black long'
                              onClick={(evt) => {
                                evt?.preventDefault();
                                onAppleGooglePayClick();
                              }}
                              disabled={!(userDetails && userDetails._id)}
                            ></button>
                          </div>
                        </React.Fragment>
                      )}
                  </>
                )}
              </>
            )}
            {localState.isError && (
              <div className='cardCommon cash active'>
                <span className='redSpan'>
                  {localState.errorMessage ? localState.errorMessage : ''}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className='totalbottom'>
          <div className='totalInner'>
            {currentOrder.publicHolidaySurcharge &&
            Number(currentOrder.publicHolidaySurcharge) !== 0 ? (
              <div className='spWrap'>
                <span className='spName'>
                  {currentOrder?.publicHolidayName
                    ? currentOrder?.publicHolidayName
                    : 'Public Holiday Surcharge'}
                  {`${
                    currentOrder &&
                    currentOrder.publicHolidaySurchargePer &&
                    Number(currentOrder.publicHolidaySurchargePer) !== 0
                      ? `(${currentOrder.publicHolidaySurchargePer}%)`
                      : null
                  }`}
                </span>
                <span className='spPrice'>{`${storeConfig.currency}${currentOrder.publicHolidaySurcharge}`}</span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder.rewards && currentOrder.rewards.length > 0 ? (
              <div className='spWrap'>
                <span className='spName'>Loyalty</span>
                <span className='spPrice'>{`- ${storeConfig.currency}${currentOrder.loyaltyDiscount}`}</span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder.specials &&
            currentOrder.specials.length > 0 &&
            Number(currentOrder.specialDiscount) > 0 ? (
              <div className='spWrap'>
                <span className='spName'>Specials</span>
                <span className='spPrice'>{`- ${storeConfig.currency}${currentOrder.specialDiscount}`}</span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder.voucherId &&
            currentOrder.voucherId !== '' &&
            Number(currentOrder.voucherDiscount) !== 0 ? (
              <div className='spWrap'>
                <span className='spName'>
                  {currentOrder.selectedVoucher.isLoyaltyVoucher
                    ? 'Reward'
                    : 'Voucher'}
                </span>
                <span className='spPrice'>{`- ${storeConfig.currency}${currentOrder.voucherDiscount} `}</span>
              </div>
            ) : (
              <div />
            )}
            {currentOrder.isDisplayGST &&
            !currentOrder.isPriceIncludesGST &&
            Number(currentOrder.gst) !== 0 ? (
              <div className='spWrap'>
                <span className='spName'>GST</span>
                <span className='spPrice'>{` ${storeConfig.currency}${currentOrder.gst} `}</span>
              </div>
            ) : (
              <div />
            )}
            {localState.cardCharges ? (
              <div className='spWrap'>
                <span className='spName'>{localState.cardChargesTitle}</span>
                <span className='spPrice'>{` ${storeConfig.currency}${localState.cardCharges} `}</span>
              </div>
            ) : (
              <div />
            )}
            <OrderUI.FooterButton
              extendedClassName='z-[1003]'
              rightIcon={
                localState.paymentInProcess ? (
                  <LoadingIcon strokeWidth={3} width={16} />
                ) : (
                  <ArrowRightIcon strokeWidth={3} width={16} />
                )
              }
              onClick={() => {
                firebaseAnalyticsNO.logEvent(
                  eventNames.SELECTED_PAYMENT_METHOD
                );
                trackAddPaymentInfo(getCurrentOrder());
                updateSelectedTimeIfItIsInPast(
                  currentOrder,
                  currentOrder.orderType === MENU_ORDER_TYPE.delivery
                    ? deliveryTimes
                    : pickUpTimes
                );
                setTimeout(() => {
                  const latestCurrentOrder = getCurrentOrder();
                  placeOrder(latestCurrentOrder);
                }, 0);
              }}
              disabled={isDisabled}
            >
              {localState.paymentInProcess
                ? 'Wait while we are processing'
                : currentOrder && Number(currentOrder.payableAmount) <= 0
                ? 'Confirm Order'
                : `Confirm and Pay ${
                    currentOrder && currentOrder.payableAmount
                      ? `${storeConfig.currency}${currentOrder.payableAmount}`
                      : `${storeConfig.currency}0.00`
                  }`}
            </OrderUI.FooterButton>
          </div>
        </div>
      </div>
    </Elements>
  );
}
